import React, { useEffect, useState, Fragment } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { backend, API_URL } from "../../config";
import { Link } from "react-router-dom";
import { jsonToCSV } from "react-papaparse";
import writeXlsxFile from "write-excel-file";

import {
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Toolbar,
  Typography,
  Paper,
  Checkbox,
  IconButton,
  Tooltip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  Input,
  TextField,
  MenuItem,
  Modal,
  Button,
  CircularProgress,
  Chip,
} from "@mui/material";

import { useDispatch, useSelector } from "react-redux";
import {
  add_product_action,
  remove_product_action,
  set_amount_product_action,
} from "../../redux/actions/cart";

import { lighten } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";

import DeleteIcon from "@mui/icons-material/Delete";
// import FilterListIcon from "@mui/icons-material/FilterList";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import LoopIcon from "@mui/icons-material/Loop";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PaidIcon from "@mui/icons-material/Paid";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import Substitution from "../Dialogs/Substitution/Substitution";
import SwapAdd from "../Dialogs/SwapAdd/SwapAdd";

const imageTypes = [".jpg", ".jpeg", ".png"];

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    maxHeight: "100vh",
  },
  container: {
    minHeight: 440,
    maxHeight: "75vh",
  },
  stickyHeader: {
    zIndex: "4 !important",
    "& [data-sticky-td=true]": {
      zIndex: "5 !important",
    },
  },
  bgRows: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  bgCells: {
    body: {
      background: "inherit",
    },
    "[data-sticky-last-left-td]": {
      "box-shadow": "2px 0px 3px #ccc",
    },
    "[data-sticky-first-right-td]": {
      "box-shadow": "-2px 0px 3px #ccc",
    },
  },
  bgCellsBody: {
    "[data-sticky-td=true]": {
      zIndex: 2,
      background: "#fff",
    },
  },
  tableBody: {
    "&:nth-of-type(even)": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  button: {
    marginTop: theme.spacing(3),
    marginLeft: theme.spacing(1),
  },
  modal: {
    position: "absolute",
    width: 400,
    backgroundColor: "#fff",
    border: "1px solid rgba(0,0,0,.2)",
    borderRadius: ".3rem",
    boxShadow: theme.shadows[5],
    padding: "1rem",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
}));

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  highlight:
    theme.palette.type === "light"
      ? {
          color: theme.palette.secondary.main,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        }
      : {
          color: theme.palette.text.primary,
          backgroundColor: lighten(theme.palette.secondary.light, 0.85),
        },
  title: {
    flex: "1 1 100%",
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const InfiniteTable = (props) => {
  const classes = useStyles();
  const {
    location,
    columns,
    dataMapByColumn,
    editable,
    deletable,
    filterable,
    label,
    fetchingModal,
    storeAddons,
  } = props;
  const staticData = props.data;

  const cartStore = useSelector((state) => state.cart);
  const customerStore = useSelector((state) => state.customer);
  const currentUserStore = useSelector((state) => state.current_user);

  const dispatch = useDispatch();

  const stagedChange = (row, action, amount, type = true) => {
    // action 1 = add, 2 = substract, 3 = amount, 4 = delete
    // type true = unit, false = case

    const itemIndex = data.findIndex((item) => item.upc === row.upc);

    if (itemIndex === -1 && action !== 1) return; // shouldnt be possible as items are already created

    const itemProp = type ? "unit_in_cart" : "case_in_cart";

    let itemObj = { ...row };
    let stagedItems = [...data];

    switch (action) {
      case 1:
        if (itemIndex === -1) {
          itemObj[itemProp] = +amount;
          stagedItems.push(itemObj);
        } else {
          stagedItems[itemIndex][itemProp] =
            stagedItems[itemIndex][itemProp] + +amount;
        }
        break;
      case 2:
        stagedItems[itemIndex][itemProp] =
          stagedItems[itemIndex][itemProp] - +amount;
        break;
      case 3:
        stagedItems[itemIndex][itemProp] = +amount;
        break;
      case 4:
        stagedItems.splice(itemIndex, 1);
        break;
      default:
        return stagedItems;
    }

    setData(stagedItems);
  };

  const add = (row, column) => {
    if (location.search.includes("stagedOrder=1")) {
      stagedChange(row, 1, 1, column === "display_unit_price");
    } else {
      dispatch(
        add_product_action(row, customerStore, column === "display_unit_price"),
      );
    }
  };

  const substract = (row, column) => {
    const existingProductInCart = location.search.includes("stagedOrder=1")
      ? data.find((product) => product.upc === row.upc)
      : cartStore[customerStore.id].products.find(
          (product) => product.upc === row.upc,
        );

    if (existingProductInCart) {
      const count =
        column === "display_unit_price"
          ? existingProductInCart.unit_in_cart
          : existingProductInCart.case_in_cart;

      if (count > 0) {
        if (location.search.includes("stagedOrder=1")) {
          stagedChange(row, 2, 1, column === "display_unit_price");
        } else {
          dispatch(
            remove_product_action(
              row,
              customerStore,
              column === "display_unit_price",
            ),
          );
        }
      }
    }
  };

  const handleAmountChange = (e, row) => {
    const { name, value } = e.target;

    const amount = value === "" ? 0 : value;

    if (location.search.includes("stagedOrder=1")) {
      stagedChange(row, 3, amount, name === "unit_in_cart");
    } else {
      dispatch(
        set_amount_product_action(
          row,
          customerStore,
          amount,
          name === "unit_in_cart",
        ),
      );
    }
  };

  const [apiep, setApiEp] = useState(props.apiep);
  const [data, setData] = useState([]);
  const [dataFull, setDataFull] = useState([]);
  const [dataFullAsync, setDataFullAsync] = useState(false);
  const [fetchData, setFetchData] = useState({ fetch: true, all: false });
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(
    label === "Order Guide" || apiep === null ? 1000 : 100,
  );
  const [selected, setSelected] = useState([]);
  const [filtering, setFiltering] = useState(false);
  const [query, setQuery] = useState("");
  const [queryColumn, setQueryColumn] = useState("");
  const [exportModal, setExportModal] = useState(false);
  const [fetchModal, setFetchModal] = useState(false);
  const [imageModal, setImageModal] = useState(false);
  const [fetchFlag, setFetchFlag] = useState(false);
  const [loadingFlag, setLoadingFlag] = useState(false);
  const [finishedFlag, setFinishedFlag] = useState(false);
  const [pendingFetch, setPendingFetch] = useState(false);
  const [imageInfo, setImageInfo] = useState({
    column: "image",
    row: {
      image:
        "https://images.freeimages.com/images/large-previews/fc3/surgeon-1-1562057.jpg",
    },
    name: "A default product",
  });
  // const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    const makeData = async () => {
      let dataMapping = { count: 0, next: "", results: [] };
      let endpoint = apiep;
      let fetchAllInEndpoint = false;

      if (endpoint == null || endpoint === "") return dataMapping;

      let keys = ["pk", "active"];
      columns.forEach((columnData) => {
        if (columnData.hasOwnProperty("id")) {
          keys.push(columnData.id);
        }
        if (
          columnData.hasOwnProperty("editablePk") &&
          !keys.includes(columnData.editablePk)
        ) {
          keys.push(columnData.editablePk);
        }
        if (columnData.hasOwnProperty("title")) {
          keys.push(columnData.title);
        }
      });

      endpoint = endpoint.replace(API_URL, "");
      fetchAllInEndpoint = endpoint.includes("no_page=1");

      if (fetchData.all || fetchAllInEndpoint) {
        let endpointBuilt = fetchAllInEndpoint
          ? endpoint
          : `${endpoint.split("?")[0]}?no_page=1`;

        await backend
          .get(`${API_URL}${endpointBuilt}`)
          .then(({ data }) => {
            if (data.length > 0) {
              const dataMap = data.map((item) => {
                const obj = {};
                keys.forEach((key) => {
                  obj[key] =
                    typeof item[key] === "object" && item[key] !== null
                      ? key === "company_location"
                        ? item[key].name
                        : key === "customer"
                          ? item[key].username
                          : item[key].pk
                      : item[key];
                });

                if (keys.includes("company_location_name")) {
                  obj["company_location_name_formatted"] = item.hasOwnProperty(
                    "company_location_name",
                  )
                    ? item.company_location_name.replaceAll(" ", "-")
                    : "";
                }

                if (keys.includes("public_name")) {
                  obj["public_name_formatted"] = item.hasOwnProperty(
                    "public_name",
                  )
                    ? item.public_name.replaceAll(" ", "-")
                    : "";
                }

                if (keys.includes("paid_status")) {
                  const isCODPaymentEnabled = Boolean(
                    item?.company_location?.cod_payments_enabled,
                  );

                  const paidStatusLabel =
                    isCODPaymentEnabled && !Boolean(item.paid_status)
                      ? "pending cod"
                      : item.paid_status_label;

                  obj["paid_status_label"] = paidStatusLabel;
                  obj["cod_payments_enabled"] =
                    item?.company_location?.cod_payments_enabled;
                  obj["invoiced"] = item.invoiced;
                }

                if (keys.includes("picking_complete")) {
                  obj["picked_total"] = item.picked_total;
                }

                return obj;
              });
              dataMapping = {
                count: dataMap.length,
                next: "",
                results: dataMap,
              };
            }
          })
          .catch((e) => {
            console.log("error", e);
          });
      } else {
        await backend
          .get(`${API_URL}${endpoint}`)
          .then(({ data }) => {
            if (data.results.length > 0) {
              const dataMap = data.results.map((item) => {
                const obj = {};
                keys.forEach((key) => {
                  obj[key] =
                    typeof item[key] === "object" && item[key] !== null
                      ? key === "customer"
                        ? item[key].username
                        : item[key].pk
                      : item[key];
                });

                if (keys.includes("company_location_name")) {
                  obj["company_location_name_formatted"] = item.hasOwnProperty(
                    "company_location_name",
                  )
                    ? item.company_location_name.replaceAll(" ", "-")
                    : "";
                }

                if (keys.includes("public_name")) {
                  obj["public_name_formatted"] = item?.public_name
                    ? item.public_name.replaceAll(" ", "-")
                    : item?.customer?.username || "Unknown Order Guide";
                }

                if (keys.includes("paid_status")) {
                  const isCODPaymentEnabled = Boolean(
                    item?.company_location?.cod_payments_enabled,
                  );

                  const paidStatusLabel =
                    isCODPaymentEnabled && !Boolean(item.paid_status)
                      ? "pending cod"
                      : item.paid_status_label;

                  obj["paid_status_label"] = paidStatusLabel;
                  obj["cod_payments_enabled"] =
                    item?.company_location?.cod_payments_enabled;
                  obj["invoiced"] = item.invoiced;
                }

                if (keys.includes("picking_complete")) {
                  obj["picked_total"] = item.picked_total;
                }

                return obj;
              });
              dataMapping = {
                count: data.count,
                next: data.next,
                results: dataMap,
              };
            }
          })
          .catch((e) => {
            console.log("error", e);
          });
      }

      setPage(0);

      return dataMapping;
    };

    if (Array.isArray(staticData)) {
      setData(staticData);
      setDataFull(staticData);
    } else if (fetchData.fetch) {
      let fullDataAsync = false;
      let nextApiep = null;
      if (!pendingFetch) {
        if (!fetchData.all) {
          setPendingFetch(true);
        }
        if (fetchingModal && !fetchFlag) {
          setFetchModal(true);
          setFetchFlag(true);
        }
        makeData()
          .then(({ count, next, results }) => {
            nextApiep = next;
            if (fetchData.all) {
              setData(results);
              setDataFull(results);
              setPendingFetch(true);
            } else {
              setData((prevState) =>
                count === prevState.length
                  ? prevState
                  : [...prevState, ...results],
              );
              setDataFull((prevState) =>
                count === prevState.length
                  ? prevState
                  : [...prevState, ...results],
              );
            }
            setApiEp(nextApiep);
          })
          .catch((e) => {
            console.log("error", e);
          })
          .finally(() => {
            setPendingFetch(false);
            if (
              apiep &&
              !apiep.includes("from_date=") &&
              !apiep.includes("to_date=")
            ) {
              fullDataAsync = true;
              if (!dataFullAsync && nextApiep !== null && nextApiep !== "") {
                setDataFullAsync(true);
                setFetchData({ fetch: true, all: true });
              }
            }
            setFetchModal(false);
          });
      }
      if (!fullDataAsync) {
        setFetchData({ fetch: false, all: fetchData.all });
      }
    }
  }, [
    apiep,
    columns,
    dataFullAsync,
    fetchData,
    fetchFlag,
    fetchingModal,
    pendingFetch,
    staticData,
  ]);

  useEffect(() => {
    const filterData = () => {
      if (filtering) {
        if (query) {
          if (queryColumn !== "") {
            setData(
              dataFull.filter((item) =>
                item[queryColumn] === undefined || item[queryColumn] === null
                  ? false
                  : item[queryColumn].toString().toLowerCase().includes(query),
              ),
            );
          } else {
            let filteredData = [];
            dataFull.forEach((item) => {
              const match = columns.some((column) =>
                item[column.id] === undefined || item[column.id] === null
                  ? false
                  : item[column.id].toString().toLowerCase().includes(query),
              );

              if (match) filteredData.push(item);
            });
            setData(filteredData);
          }
        } else {
          setData(dataFull);
        }
      }
    };

    filterData();
  }, [columns, dataFull, filtering, query, queryColumn]);

  useEffect(() => {
    if (imageInfo.name !== "A default product") {
      setImageModal((prevState) => !prevState);
    }
  }, [imageInfo]);

  const handleChange = (e) => {
    const { checked, name, value } = e.target;
    const [columnId, rowId] = name.split("-");

    /*if (value.includes("_custom_substitution_")) {
      setSubstitutionSettings((prevState) => ({
        ...prevState,
        order_id: orderItems[rowId].order,
        original_product: orderItems[rowId],
      }));
      toggleProductModal();
    } else {*/
    const columnData = columns.find((item) => item.id === columnId);
    if (columnData.exposed) {
      setData((prevState) =>
        prevState.map((item, index) => {
          if (+rowId === index) {
            const columnValue = columnData.hasOwnProperty("exposedAs")
              ? columnData.exposedAs === "checkbox"
                ? checked
                : value
              : value;
            item[columnId] = columnValue;
            if (columnId === "unit_quantity" || columnId === "case_quantity") {
              const estimated_price =
                item.unit_quantity * item.display_unit_price +
                item.case_quantity * item.display_case_price;
              item["estimated_price"] = estimated_price.toFixed(2);
            }
            item["modified"] = true;

            return item;
          } else {
            return item;
          }
        }),
      );

      setDataFull((prevState) =>
        prevState.map((item, index) => {
          if (+rowId === index) {
            const columnValue = columnData.hasOwnProperty("exposedAs")
              ? columnData.exposedAs === "checkbox"
                ? checked
                : value
              : value;
            item[columnId] = columnValue;

            return item;
          } else {
            return item;
          }
        }),
      );
    }
    // }
  };

  const handleQuery = (e) => {
    /* clearTimeout(delayDebounceFn);
    delayDebounceFn(e); */
    setQuery(e.target.value.toLowerCase());
  };

  const handleQueryColumn = (e) => {
    setQueryColumn(e.target.value);
  };

  /* const handleChangeIncludeInactive = (e) => {
    const { checked } = e.target;

    setIncludeInactive(checked);
  }; */

  /* const delayDebounceFn = (e) =>
    setTimeout(() => {
      setQuery(e.target.value.toLowerCase());
    }, 500); */

  const doFetchData = (e) => {
    if (
      e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight &&
      !pendingFetch
    ) {
      setFetchData({ fetch: true, all: false });
    }
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((n) => n.name);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    if (!deletable) return;
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }

    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    document.getElementById("tableContainer").scrollTop = 0;
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const isSelected = (name) => selected.indexOf(name) !== -1;

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, data.length - page * rowsPerPage);

  const descendingComparator = (a, b, orderBy) => {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  };

  const getComparator = (order, orderBy) => {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  const stableSort = (array, comparator) => {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  const cellFormat = (value, row, column, rowIndex = null) => {
    const columnId = isNaN(column) ? column.id : column;

    if (column.hasOwnProperty("exposed")) {
      const exposedAs = column.exposedAs || "input";
      switch (exposedAs) {
        case "input":
        default:
          return (
            <Input
              name={`${column.id}-${rowIndex}`}
              value={value}
              onChange={handleChange}
              disabled={
                (column.id === "unit_quantity" &&
                  row.display_unit_price <= 0) ||
                (column.id === "case_quantity" && row.display_case_price <= 0)
              }
            />
          );
        case "checkbox":
          return (
            <Checkbox
              name={`${column.id}-${rowIndex}`}
              value={value}
              checked={!!value}
              onChange={handleChange}
              disabled={column.id === "picked"}
            />
          );
        case "select":
          return value === false ? (
            <></>
          ) : column.id === "name" ? (
            <Select
              fullWidth
              input={<Input />}
              name={`${column.id}-${rowIndex}`}
              value={row}
              onChange={handleChange}
            >
              <MenuItem value={row}>{row.name}</MenuItem>
              <MenuItem value={`_custom_substitution_${rowIndex}`}>
                Custom substitution
              </MenuItem>
            </Select>
          ) : (
            <Select
              fullWidth
              input={<Input />}
              name={`${column.id}-${rowIndex}`}
              value={value}
              onChange={handleChange}
              MenuProps={MenuProps}
            >
              <MenuItem value={value}>{"" + value}</MenuItem>
            </Select>
          );
      }
    } else if (columnId === "_acceptable_substitution_") {
      return storeAddons ? <SwapAdd data={row} type={2} /> : <></>;
    } else if (columnId === "size") {
      return row.display_unit_price_per_lb || row.display_case_price_per_lb ? (
        <>
          {row.unit_type === "Unit" || row.unit_type === "Unit / Case" ? (
            <>U: {`$${row.display_unit_price_per_lb || 0}`}</>
          ) : (
            <></>
          )}
          {row.unit_type === "Unit / Case" ? <br /> : <></>}
          {row.unit_type === "Case" || row.unit_type === "Unit / Case" ? (
            <>C: {`$${row.display_case_price_per_lb || 0}`}</>
          ) : (
            <></>
          )}
        </>
      ) : (
        row.size
      );
    } else if (columnId === "paid_status") {
      const isCODPaymentEnabled = Boolean(row?.cod_payments_enabled);

      return (
        <div style={{ textAlign: "center" }}>
          <Chip
            color={
              ["p", "e", "c"].includes(value)
                ? "success"
                : ["r", "x"].includes(value)
                  ? "error"
                  : value === "i"
                    ? "info"
                    : isCODPaymentEnabled && !Boolean(value)
                      ? "warning"
                      : "default"
            }
            label={
              isCODPaymentEnabled || (value !== null && value !== "")
                ? row.paid_status_label.replaceAll("_", " ")
                : "N/A"
            }
            {...(value === "r"
              ? {
                  onClick: () => props.history.push(`/order/${row.id}/invoice`),
                }
              : undefined)}
          />
        </div>
      );
    } else if (columnId === "picking_complete") {
      return (
        <div style={{ textAlign: "center" }}>
          <Tooltip
            title={
              row.picking_complete ? "Picking Complete" : "Picking Pending"
            }
          >
            <CheckCircleIcon
              color={row.picking_complete ? "success" : "disabled"}
            />
          </Tooltip>
        </div>
      );
    } else {
      if (column.id === "price_per_type") {
        return (
          <>
            {row.unit_quantity > 0 ? <>U: {`$${row.price_per_unit}`}</> : <></>}
            {row.unit_quantity > 0 && row.case_quantity > 0 ? <br /> : <></>}
            {row.case_quantity > 0 ? <>C: {`$${row.price_per_case}`}</> : <></>}
          </>
        );
      }

      switch (typeof value) {
        case "string":
          const dateVal = new Date(value);
          const dateFix = new Date(
            dateVal.getTime() - dateVal.getTimezoneOffset() * 60000,
          );
          const containsLetter = value.match(/([A-Za-z])/g);
          if (
            !isNaN(dateFix.getTime()) &&
            containsLetter &&
            (!containsLetter.length ||
              (value.includes("T") && value.includes(":")))
          ) {
            return dateFix.toISOString().split("T")[0];
          } else if (
            value.indexOf("https://") === 0 ||
            value.indexOf("http://") === 0
          ) {
            const isImg = imageTypes.some((itype) =>
              value.toLocaleLowerCase().indexOf(itype),
            );

            if (!isImg) {
              return value;
            }

            const dirs = value.split("/");
            const filename = dirs.pop();
            const imgInfo = { column: columnId, name: filename, row: row };

            const displayImageComp = (
              <Tooltip title="View image">
                <IconButton
                  aria-label="view image"
                  onClick={() => {
                    setImageInfo(imgInfo);
                  }}
                >
                  <OpenInNewIcon />
                </IconButton>
              </Tooltip>
            );

            return displayImageComp;
          } else if (column.id === "name" && row.weight_type === "r/w") {
            return <b>{value}</b>;
          } else {
            return value;
          }
        case "number":
          if (
            storeAddons &&
            (columnId === "display_unit_price" ||
              columnId === "display_case_price")
          ) {
            const existingProductInCart = location.search.includes(
              "stagedOrder=1",
            )
              ? data.find((product) => product.upc === row.upc)
              : cartStore[customerStore.id].products.find(
                  (product) => product.upc === row.upc,
                );

            const cartComp = (
              <>
                <div style={{ textAlign: "center" }}>{`$${value.toFixed(2)} ${
                  row.api_catch_weight
                    ? "(" +
                      (columnId === "display_unit_price"
                        ? row.average_unit_weight
                        : row.average_case_weight) +
                      ")"
                    : columnId === "display_case_price"
                      ? "(" + row.units_per_case + ")"
                      : ""
                }`}</div>
                {value > 0 ? (
                  <div style={{ minWidth: 90, textAlign: "center" }}>
                    {location.search.includes("stagedOrder=1") ? (
                      <></>
                    ) : (
                      <IconButton
                        onClick={() => substract(row, columnId)}
                        style={{ padding: 0 }}
                      >
                        <RemoveIcon color="error" />
                      </IconButton>
                    )}
                    <TextField
                      size="small"
                      variant="standard"
                      name={
                        columnId === "display_unit_price"
                          ? "unit_in_cart"
                          : "case_in_cart"
                      }
                      value={
                        existingProductInCart
                          ? columnId === "display_unit_price"
                            ? existingProductInCart.unit_in_cart
                            : existingProductInCart.case_in_cart
                          : 0
                      }
                      onChange={(e) => handleAmountChange(e, row)}
                      style={{ maxWidth: 40 }}
                      inputProps={{
                        style: { textAlign: "center" },
                        readOnly: true,
                      }}
                    />
                    {location.search.includes("stagedOrder=1") ? (
                      <></>
                    ) : (
                      <IconButton
                        onClick={() => add(row, columnId)}
                        style={{ padding: 0 }}
                      >
                        <AddIcon color="success" />
                      </IconButton>
                    )}
                  </div>
                ) : (
                  <></>
                )}
              </>
            );

            return cartComp;
          } else if (
            column.id.includes("price") ||
            column.id.includes("total")
          ) {
            if (label === "Orders" && column.id === "total") {
              const localUser = JSON.parse(localStorage.user);

              if (localUser.user.is_staff) return "$" + value.toFixed(2);

              return row["invoice_total"] === null
                ? "Pending"
                : row["invoice_total"];
            }
            return "$" + value.toFixed(2);
          } else if (column.id.includes("quantity")) {
            /* const unitType = column.id.replace("_quantity", "");
            const pickedValue = row[`${unitType}s_picked`];

            return value !== pickedValue ? `${pickedValue} of ${value}` : value; */
            return value;
          } else {
            return value;
          }
        /* todo: check the behaviour of upc displayed as exponential numbers when adding receipt data
          } else if (columnId === "upc" || columnId === "product") {
            return value.toString();
          } */
        case "boolean":
          return value.toString();
        case "object":
          if (value === null) return "N/A";

          if (column.hasOwnProperty(column.innerId)) {
            if (!value[column.innerId]) return "N/A";

            return value[column.innerId];
          }

          const innerValue = value.hasOwnProperty(column.id)
            ? value[column.id]
            : "N/A";

          if (
            ["item_code", "item_codee"].includes(column.id) &&
            row.weight_type === "r/w"
          ) {
            return <b>{innerValue}</b>;
          }

          return innerValue;
        default:
          return value;
      }
    }
  };

  const filterDataToExport = (data, columnsToExport, excel = false) => {
    let itemData = {};

    const dataExportMap = data.map((item) => {
      if (excel) {
        itemData = [];
      } else {
        itemData = {};
      }

      columnsToExport.forEach((column) => {
        let columnVal = item.hasOwnProperty(column.id) ? item[column.id] : "";

        const isOrderList = columns.find(
          (cItem) => cItem.id === "od_category_name",
        );

        if (isOrderList && column.id.includes("price")) {
          columnVal = item[column.id] > 0 ? "" : "X";
        }

        if (typeof columnVal === "object" && columnVal !== null) {
          if (columnVal[column.id]) {
            columnVal = columnVal[column.id];
          } else {
            columnVal = "";
          }
        }

        if (excel) {
          let cellObj = {
            value: columnVal,
            borderStyle: "thin",
            alignVertical: "center",
            wrap: true,
          };

          if (isOrderList && column.id.includes("price")) {
            cellObj["align"] = "center";
          }

          if (column.id === "upc") {
            cellObj["value"] = "" + cellObj["value"];
            cellObj["type"] = String;
          }

          itemData.push(cellObj);
        } else {
          itemData[column.label] = columnVal;
        }
      });

      return itemData;
    });

    if (!excel) return dataExportMap;

    const dataExportHeaders = columnsToExport.map((cItem) => ({
      value: cItem.label,
      borderStyle: "thin",
      fontWeight: "bold",
    }));

    return [dataExportHeaders, ...dataExportMap];
  };

  const exportData = async () => {
    setLoadingFlag(true);

    const cDate = new Date(
      new Date().toString().split("GMT")[0] + " UTC",
    ).toISOString();
    const filename = `${label} ${cDate}`;
    const columnsToExport = columns.filter((column) =>
      column.hasOwnProperty("exportable") ? column.exportable : true,
    );
    const excel = true;

    const dataToExport = filterDataToExport(data, columnsToExport, excel);

    if (excel) {
      let columnsWidth = [];

      const isOrderList = columnsToExport.find(
        (cItem) => cItem.id === "od_category_name",
      );

      if (isOrderList) {
        columnsWidth = [
          { width: 18 },
          { width: 11 },
          { width: 11 },
          { width: 48 },
          { width: 6.5 },
          { width: 6.5 },
        ];
      } else {
        let columnsWidthRaw = columnsToExport.map(() => 0);

        dataToExport.forEach((item) => {
          columnsWidthRaw = columnsWidthRaw.map((width, index) => {
            const valLength = item[index].value
              ? item[index].value.toString().length
              : 0;

            if (width >= valLength) {
              return width;
            } else {
              return valLength;
            }
          });
        });

        columnsWidth = columnsWidthRaw.map((cWidth) => ({
          width: cWidth + 2,
        }));
      }

      await writeXlsxFile(dataToExport, {
        fileName: `${filename}.xlsx`,
        columns: columnsWidth,
      });
    } else {
      const results = jsonToCSV(dataToExport);
      const blob = new Blob([`${results}`], {
        type: "text/csv;charset=utf-8;",
      });
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `${filename}.csv`);
      link.click();
      link.remove();
    }
    setLoadingFlag(false);
    setFinishedFlag(true);
  };

  const filterUnpaidOrders = () => {
    let filteredData =
      dataFull.length !== data.length
        ? dataFull
        : dataFull.filter((item) => ![null, ""].includes(item.paid_status));

    setPage(0);
    setData(filteredData);
  };

  const resolveRequest = () => {
    toggleExportModal();
    setFinishedFlag(false);
  };

  const toggleExportModal = () => setExportModal(!exportModal);

  const toggleImageModal = () => setImageModal(!imageModal);

  const exportModalContent = (
    <div className={classes.modal}>
      {!loadingFlag && !finishedFlag ? (
        <>
          Would you like to export the <b>current</b> table data?
          <div align="right">
            <Button
              onClick={() => toggleExportModal()}
              className={classes.button}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => exportData()}
              className={classes.button}
            >
              Export
            </Button>
          </div>
        </>
      ) : loadingFlag ? (
        <>
          <div style={{ textAlign: "center" }}>
            <CircularProgress />
            <div>Exporting...</div>
          </div>
        </>
      ) : (
        <>
          Table data has been exported to CSV successfully check your downloads
          section.
          <div align="right">
            <Button
              variant="contained"
              color="primary"
              onClick={() => resolveRequest()}
              className={classes.button}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const fetchModalContent = (
    <div className={classes.modal}>
      <div align="center">
        <CircularProgress />
        <div>
          {fetchingModal ? fetchingModal.title : "Fetching"}
          <br />
          {fetchingModal
            ? fetchingModal.description
            : "This may take some time relative to the number of records."}
        </div>
      </div>
    </div>
  );

  const imageModalContent = (
    <div className={classes.modal}>
      <div align="center">
        <img
          alt={
            imageInfo["row"]["name"]
              ? imageInfo["row"]["name"]
              : imageInfo["name"]
          }
          src={imageInfo["row"][imageInfo["column"]]}
          width="100%"
          height="auto"
        />
        <div>
          {imageInfo["row"]["name"]
            ? imageInfo["row"]["name"]
            : imageInfo["name"]}
        </div>
      </div>
    </div>
  );

  const EnhancedTableHead = (props) => {
    const {
      onSelectAllClick,
      order,
      orderBy,
      numSelected,
      rowCount,
      onRequestSort,
    } = props;

    const createSortHandler = (property) => (event) => {
      onRequestSort(event, property);
    };

    return (
      <TableHead>
        <TableRow>
          {deletable ? (
            <TableCell padding="checkbox">
              <Checkbox
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{ "aria-label": "select all" }}
              />
            </TableCell>
          ) : (
            <></>
          )}
          {columns
            .filter((headCell) => {
              let visible = true;

              if (headCell.hasOwnProperty("visibility_deny_rid")) {
                visible = currentUserStore
                  ? !headCell.visibility_deny_rid.includes(
                      currentUserStore.role_id,
                    )
                  : false;
              }

              if (headCell.hasOwnProperty("visibility_allow_rid")) {
                visible = currentUserStore
                  ? headCell.visibility_allow_rid.includes(
                      currentUserStore.role_id,
                    )
                  : false;
              }

              if (
                headCell.id === "picking_complete" ||
                headCell.id === "invoice_total"
              ) {
                const localUser = JSON.parse(localStorage.user);

                const canSeePickedInfo = localUser.user.is_staff;

                if (!canSeePickedInfo) {
                  visible = false;
                }
              }

              if (headCell.id === "paid_status") {
                const localUser = JSON.parse(localStorage.user);

                const canSeePaymentInfo =
                  localUser.user.can_see_payments_column;

                if (!canSeePaymentInfo) {
                  visible = false;
                }
              }

              return visible;
            })
            .map((headCell, index) => (
              <TableCell
                key={index}
                align={headCell.numeric ? "right" : "left"}
                sortDirection={orderBy === headCell.id ? order : false}
                style={{
                  width: ["url", "_acceptable_substitutions"].includes(
                    headCell.id,
                  )
                    ? 40
                    : "inherit",
                  padding: ["url", "_acceptable_substitutions"].includes(
                    headCell.id,
                  )
                    ? 0
                    : "auto",
                }}
              >
                <TableSortLabel
                  active={orderBy === headCell.id}
                  direction={orderBy === headCell.id ? order : "asc"}
                  onClick={createSortHandler(headCell.id)}
                >
                  {headCell.label}
                </TableSortLabel>
              </TableCell>
            ))}
        </TableRow>
      </TableHead>
    );
  };

  EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(["asc", "desc"]).isRequired,
    orderBy: PropTypes.string,
    rowCount: PropTypes.number.isRequired,
  };

  const EnhancedTableToolbar = (props) => {
    const classes = useToolbarStyles();
    const { numSelected, filtering } = props;

    return (
      <Toolbar
        key="toolbar"
        className={clsx(classes.root, {
          [classes.highlight]: numSelected > 0 || filtering,
        })}
      >
        {numSelected > 0 ? (
          <Typography
            className={classes.title}
            color="inherit"
            variant="subtitle1"
            component="div"
          >
            {numSelected} selected
          </Typography>
        ) : (
          <Fragment key="filtering-fragment-container">
            {filtering ? (
              <Grid
                key="filtering-container"
                container
                spacing={2}
                alignItems="center"
              >
                <Grid
                  key="search-column-container"
                  item
                  xs={3}
                  sm={3}
                  style={{ paddingTop: 0 }}
                >
                  <FormControl fullWidth>
                    <InputLabel
                      htmlFor="search-column"
                      style={{ marginTop: -4 }}
                    >
                      Search by column
                    </InputLabel>
                    <Select
                      key="search-column"
                      id="search-column"
                      input={<Input />}
                      name="search-column"
                      defaultValue={queryColumn}
                      onChange={handleQueryColumn}
                    >
                      <MenuItem key={"all"} value="">
                        <em>All</em>
                      </MenuItem>
                      {columns
                        .filter((headCell) => {
                          let visible = true;

                          if (headCell.hasOwnProperty("visibility_deny_rid")) {
                            visible = currentUserStore
                              ? !headCell.visibility_deny_rid.includes(
                                  currentUserStore.role_id,
                                )
                              : false;
                          }

                          if (headCell.hasOwnProperty("visibility_allow_rid")) {
                            visible = currentUserStore
                              ? headCell.visibility_allow_rid.includes(
                                  currentUserStore.role_id,
                                )
                              : false;
                          }

                          return visible;
                        })
                        .map((headCell, index) => (
                          <MenuItem key={"column-" + index} value={headCell.id}>
                            {headCell.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid key="search-text-container" item xs={true} sm={true}>
                  <TextField
                    fullWidth
                    autoFocus
                    key="search-text"
                    size="small"
                    id="search-text"
                    variant="outlined"
                    label="Search "
                    name="search-text"
                    defaultValue={query}
                    onChange={handleQuery}
                  />
                </Grid>
                {/* <Grid
                  key="search-inactive-container"
                  item
                  xs={"auto"}
                  sm={"auto"}
                >
                  <FormControlLabel
                    value="start"
                    control={
                      <Checkbox
                        name="include_inactive"
                        value={includeInactive}
                        checked={includeInactive}
                        onChange={handleChangeIncludeInactive}
                        color="primary"
                      />
                    }
                    label="Show Inactive"
                    labelPlacement="end"
                  />
                </Grid> */}
              </Grid>
            ) : (
              <Typography
                className={classes.title}
                variant="h6"
                id="tableTitle"
                component="div"
              >
                {label}
              </Typography>
            )}
          </Fragment>
        )}
        {label === "Orders" &&
          currentUserStore &&
          currentUserStore.is_staff && (
            <Tooltip title="Filter paid">
              <IconButton
                aria-label="filter paid"
                onClick={() => {
                  filterUnpaidOrders();
                }}
              >
                <PaidIcon />
              </IconButton>
            </Tooltip>
          )}
        <Tooltip title="Export list">
          <IconButton
            aria-label="export list"
            onClick={() => {
              toggleExportModal();
            }}
          >
            <SaveAltIcon />
          </IconButton>
        </Tooltip>
        {numSelected > 0 ? (
          <Tooltip title="Delete">
            <IconButton aria-label="delete">
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <>
            {filterable ? (
              <Tooltip title="Search options">
                <IconButton
                  aria-label="search options"
                  onClick={(e) => {
                    setFiltering(!filtering);
                  }}
                >
                  <SearchIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <></>
            )}
          </>
        )}
      </Toolbar>
    );
  };

  EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
    filtering: PropTypes.bool.isRequired,
  };

  return (
    <Fragment key="fragment-container">
      <CssBaseline />
      <Modal open={fetchModal}>{fetchModalContent}</Modal>
      <Modal open={exportModal} onClose={toggleExportModal}>
        {exportModalContent}
      </Modal>
      <Modal open={imageModal} onClose={toggleImageModal}>
        {imageModalContent}
      </Modal>
      <div key="div-container" className={classes.root}>
        <Paper key="paper-container" className={classes.root}>
          <EnhancedTableToolbar
            key="enhanced-toolbar"
            numSelected={selected.length}
            filtering={filtering}
          />
          <TableContainer
            className={classes.container}
            onScroll={doFetchData}
            id="tableContainer"
          >
            <Table
              className={classes.table}
              stickyHeader
              aria-label="sticky enhanced table"
              size="medium"
            >
              <EnhancedTableHead
                classes={classes}
                numSelected={selected.length}
                order={order}
                orderBy={orderBy}
                onSelectAllClick={handleSelectAllClick}
                onRequestSort={handleRequestSort}
                rowCount={data.length}
              />
              <TableBody>
                {stableSort(data, getComparator(order, orderBy))
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, rowIndex) => {
                    const isItemSelected = isSelected(rowIndex);
                    const labelId = `enhanced-table-checkbox-${rowIndex}`;

                    return (
                      <TableRow
                        hover
                        onClick={(event) => handleClick(event, rowIndex)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={rowIndex}
                        selected={isItemSelected}
                        className={`${classes.tableBody}${
                          row.active === false ? " iInactive" : ""
                        }${row.out_of_stock ? " iOOS" : ""}${
                          currentUserStore &&
                          currentUserStore.is_staff &&
                          row.invoiced
                            ? " iInvoiced"
                            : ""
                        }`}
                      >
                        {deletable ? (
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              inputProps={{ "aria-labelledby": labelId }}
                            />
                          </TableCell>
                        ) : (
                          <></>
                        )}
                        {columns
                          .filter((headCell) => {
                            let visible = true;

                            if (
                              headCell.hasOwnProperty("visibility_deny_rid")
                            ) {
                              visible = currentUserStore
                                ? !headCell.visibility_deny_rid.includes(
                                    currentUserStore.role_id,
                                  )
                                : false;
                            }

                            if (
                              headCell.hasOwnProperty("visibility_allow_rid")
                            ) {
                              visible = currentUserStore
                                ? headCell.visibility_allow_rid.includes(
                                    currentUserStore.role_id,
                                  )
                                : false;
                            }

                            if (
                              headCell.id === "picking_complete" ||
                              headCell.id === "invoice_total"
                            ) {
                              const localUser = JSON.parse(localStorage.user);

                              const canSeePickedInfo = localUser.user.is_staff;

                              if (!canSeePickedInfo) {
                                visible = false;
                              }
                            }

                            if (headCell.id === "paid_status") {
                              const localUser = JSON.parse(localStorage.user);

                              const canSeePaymentInfo =
                                localUser.user.can_see_payments_column;

                              if (!canSeePaymentInfo) {
                                visible = false;
                              }
                            }

                            return visible;
                          })
                          .map((column, columnIndex) => (
                            <TableCell
                              key={rowIndex + "-" + columnIndex}
                              style={{
                                width: [
                                  "url",
                                  "_acceptable_substitutions",
                                ].includes(column.id)
                                  ? 40
                                  : "inherit",
                                padding: [
                                  "url",
                                  "_acceptable_substitutions",
                                ].includes(column.id)
                                  ? 0
                                  : "auto",
                              }}
                            >
                              {editable &&
                              column.editable &&
                              column.editablePk ? (
                                <Link
                                  to={{
                                    pathname: `${column.editable}/${
                                      row[column.editablePk]
                                    }`,
                                    state: { data: row },
                                  }}
                                >
                                  <span
                                    className={
                                      rowIndex === 0 ? "edit-link" : undefined
                                    }
                                  >
                                    {dataMapByColumn === "id"
                                      ? row[column.id]
                                      : row[columnIndex]}
                                  </span>
                                </Link>
                              ) : dataMapByColumn === "id" ? (
                                cellFormat(
                                  row[column.id],
                                  row,
                                  column,
                                  rowIndex,
                                )
                              ) : (
                                cellFormat(row[columnIndex], row, columnIndex)
                              )}
                              {column.id === "upc" ||
                              column.id === "product" ? (
                                <>
                                  <Substitution data={row} />
                                  {row.out_of_stock && (
                                    <Tooltip
                                      title="Out of stock"
                                      style={{ verticalAlign: "middle" }}
                                    >
                                      <BlockIcon color="error" />
                                    </Tooltip>
                                  )}
                                </>
                              ) : (
                                <></>
                              )}
                            </TableCell>
                          ))}
                      </TableRow>
                    );
                  })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 53 * emptyRows }}>
                    <TableCell colSpan={6} />
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Grid
            container
            className={classes.root}
            style={{ padding: "0px 10px" }}
          >
            <Grid item xs={1} sm={1}>
              <Tooltip title="Load all data">
                <IconButton
                  aria-label="load all data"
                  onClick={(e) => {
                    if (
                      apiep !== null &&
                      apiep !== "" &&
                      !apiep.includes("from_date") &&
                      !apiep.includes("to_date")
                    ) {
                      setFetchData({ fetch: true, all: true });
                      setPendingFetch(false);
                    }
                  }}
                >
                  <LoopIcon />
                </IconButton>
              </Tooltip>
            </Grid>
            <Grid item xs={11} sm={11}>
              <TablePagination
                rowsPerPageOptions={[25, 75, 100, 1000]}
                component="div"
                count={data.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </Grid>
        </Paper>
      </div>
    </Fragment>
  );
};

export default InfiniteTable;
